/**
 * Display text for a PDF link: the decoded file name without query params,
 * e.g. `https://x.com/docs/SCC%20Paving.pdf?sig=abc` → `SCC Paving.pdf`.
 */
export function getPdfFileName(pdfLink: string): string {
  const fileName = pdfLink.split('?')[0].split('/').pop() || 'PDF';

  try {
    return decodeURIComponent(fileName);
  } catch {
    // Malformed percent-encoding (e.g. a stray "100%") — show it as-is.
    return fileName;
  }
}

/**
 * Absolute http(s) URL safe to use as an href, tolerating protocol-less
 * values, e.g. `www.malibucity.org` → `https://www.malibucity.org`.
 * Returns undefined for values that can't become a plausible web URL
 * (unparseable text, non-http schemes, hosts without a dot).
 */
export function toAbsoluteHttpUrl(link: string): string | undefined {
  const trimmed = link.trim();

  if (/^[a-z][a-z0-9+.-]*:/i.test(trimmed) && !/^https?:\/\//i.test(trimmed)) {
    return undefined;
  }

  const withProtocol = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;

  try {
    return new URL(withProtocol).host.includes('.') ? withProtocol : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Display text for an agency link: the host, tolerating protocol-less values,
 * e.g. `https://dpw.lacounty.gov/paving` → `dpw.lacounty.gov`.
 * Falls back to the raw value when it can't be parsed as a URL.
 */
export function getLinkHost(link: string): string {
  const trimmed = link.trim();

  try {
    return new URL(/^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`).host || trimmed;
  } catch {
    return trimmed;
  }
}
